/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useLocation } from '@tanstack/react-router'

import { AnimatedOutlet } from '@/components/page-transition'
import { SkipToMain } from '@/components/skip-to-main'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { LayoutProvider } from '@/context/layout-provider'
import { SearchProvider } from '@/context/search-provider'
import { getCookie } from '@/lib/cookies'
import { useRollingAnnouncement } from '@/hooks/use-rolling-announcement'
import { cn } from '@/lib/utils'

import { AppHeader } from './app-header'
import { AppSidebar } from './app-sidebar'
import { RollingAnnouncement } from './rolling-announcement'

type AuthenticatedLayoutProps = {
  children?: React.ReactNode
}

export function AuthenticatedLayout(props: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie('sidebar_state') !== 'false'
  const rollingAnnouncement = useRollingAnnouncement()
  const pathname = useLocation({ select: (location) => location.pathname })
  const isFullWidthPage =
    pathname === '/my-scripts' || pathname === '/my-scripts/'

  return (
    <LayoutProvider>
      <SearchProvider>
        <SidebarProvider
          defaultOpen={defaultOpen}
          className='flex-col'
          style={
            {
              '--app-marquee-height': rollingAnnouncement.visible
                ? '2.25rem'
                : '0px',
            } as React.CSSProperties
          }
        >
          <SkipToMain />
          <AppHeader showSidebarTrigger={!isFullWidthPage} />
          <RollingAnnouncement
            content={rollingAnnouncement.content}
            visible={rollingAnnouncement.visible}
            onDismiss={rollingAnnouncement.dismiss}
          />
          <div className='flex min-h-0 w-full flex-1'>
            {isFullWidthPage ? null : <AppSidebar />}
            <SidebarInset
              className={cn(
                '@container/content',
                'h-[calc(100svh-var(--app-header-height,0px)-var(--app-marquee-height,0px))]',
                'min-h-0 overflow-hidden',
                'peer-data-[variant=inset]:h-[calc(100svh-var(--app-header-height,0px)-var(--app-marquee-height,0px)-(var(--spacing)*4))]'
              )}
            >
              {props.children ?? <AnimatedOutlet />}
            </SidebarInset>
          </div>
        </SidebarProvider>
      </SearchProvider>
    </LayoutProvider>
  )
}
